/**
 * The packet contract.
 *
 * Everything a client may say is described here as a strict Zod schema, and the server
 * accepts nothing that does not parse. Two properties of this file are load-bearing:
 *
 * **No inbound message carries a user id.** Not one. The 2023 dispatchers read
 * `challengerId` and `acceptorId` out of the payload, so anyone could post a challenge
 * as anyone and accept one as anyone. Here the actor is not merely ignored when it
 * arrives — there is no field for it to arrive in, and the server takes the actor from
 * the connection that sent the packet.
 *
 * **Objects are strict.** An unexpected key is a parse failure rather than a silently
 * dropped one, so a payload that tries to smuggle a `userId` past a future careless
 * dispatcher is rejected at the boundary instead of reaching it.
 *
 * Variant bounds are checked here too. The old codec validated that `radius` was a
 * number but not that it was a *sane* number, so a single crafted challenge could ask
 * the server to build an arbitrarily large board.
 */

import { z } from 'zod';
import { FORMATION_NAMES, formationInfo, MAX_RADIUS, MIN_RADIUS } from '@gok/engine';
import type { FormationName, Variant } from '@gok/engine';

/** Bumped when a change makes old clients wrong rather than merely out of date. */
export const PROTOCOL_VERSION = 1;

export const MAX_USERNAME = 24;
export const MAX_CHAT = 400;
/** Long enough for `a12*b13#`, with room to spare. */
export const MAX_SAN = 16;

const ID = z.string().regex(/^[0-9a-z]{4,40}$/, 'malformed id');
const BASE64URL = (bytes: number) =>
  z.string().regex(
    new RegExp(
      `^[A-Za-z0-9_-]{${Math.ceil(bytes * 4 / 3)},${Math.ceil(bytes * 4 / 3) + 2}}$`,
    ),
    `expected ${bytes} bytes of base64url`,
  );

export const UsernameSchema = z
  .string()
  .trim()
  .min(2)
  .max(MAX_USERNAME)
  // Letters, digits, and single internal separators. Deliberately narrow: names are
  // rendered next to each other in the lobby, and lookalike whitespace and
  // right-to-left overrides are how one player impersonates another.
  .regex(/^[A-Za-z0-9]+(?:[ _-][A-Za-z0-9]+)*$/, 'letters and digits, single separators');

export const VariantSchema = z
  .strictObject({
    radius: z.int().min(MIN_RADIUS).max(MAX_RADIUS),
    formation: z.enum([...FORMATION_NAMES] as [FormationName, ...FormationName[]]),
    spawnsAvailable: z.int().min(0).max(40),
    timeInitialMs: z.int().min(10_000).max(4 * 60 * 60_000),
    timeIncrementMs: z.int().min(0).max(120_000),
    stakes: z.int().min(0).max(3),
  })
  .refine((v) => v.radius >= formationInfo(v.formation).minRadius, {
    message: 'that formation does not fit on a board that small',
  }) satisfies z.ZodType<Variant, unknown>;

/**
 * A channel name. `lobby`, `user-<id>`, `match-<id>` — nothing else, and the id shape is
 * checked here so a subscription cannot be used to probe for arbitrary keys.
 */
export const ChannelSchema = z
  .string()
  .max(64)
  .regex(/^(lobby|user-[0-9a-z]{4,40}|match-[0-9a-z]{4,40})$/, 'unknown channel');

export const ClientMessageSchema = z.discriminatedUnion('t', [
  // --- handshake ---
  z.strictObject({
    t: z.literal('identify'),
    publicKey: BASE64URL(32),
    signature: BASE64URL(64),
  }),
  z.strictObject({
    t: z.literal('register'),
    username: UsernameSchema,
    password: z.string().min(8).max(200),
  }),
  z.strictObject({
    t: z.literal('login'),
    username: UsernameSchema,
    password: z.string().min(1).max(200),
  }),

  // --- subscriptions ---
  z.strictObject({ t: z.literal('sub'), ch: ChannelSchema }),
  z.strictObject({ t: z.literal('unsub'), ch: ChannelSchema }),

  // --- lobby ---
  z.strictObject({ t: z.literal('setName'), username: UsernameSchema }),
  z.strictObject({ t: z.literal('challenge'), variant: VariantSchema }),
  z.strictObject({ t: z.literal('unchallenge'), id: ID }),
  z.strictObject({ t: z.literal('accept'), id: ID }),

  // --- match ---
  z.strictObject({ t: z.literal('move'), matchId: ID, san: z.string().max(MAX_SAN) }),
  z.strictObject({
    t: z.literal('chat'),
    matchId: ID,
    text: z.string().min(1).max(MAX_CHAT),
  }),

  z.strictObject({ t: z.literal('ping'), at: z.number() }),
]);

export type ClientMessage = z.infer<typeof ClientMessageSchema>;
export type ClientMessageType = ClientMessage['t'];

/**
 * Error codes the server sends back. Named rather than numbered so a log line says what
 * went wrong without a lookup table.
 */
export type ErrorCode =
  | 'protocol' // malformed or unparseable
  | 'unauthenticated' // said something before completing the handshake
  | 'auth' // handshake failed: bad signature, stale nonce, wrong password
  | 'taken' // username already registered
  | 'notFound'
  | 'illegal' // a move the rules refuse
  | 'forbidden' // legal packet, wrong actor
  | 'rateLimit';

export type ServerMessage =
  /** First frame on every socket: the nonce the client must sign. */
  | {
    t: 'challenge';
    nonce: string;
    protocol: number;
    serverTime: number;
  }
  /** Handshake complete. Everything after this is attributable to `userId`. */
  | { t: 'welcome'; userId: string; username: string; serverTime: number }
  /** Full state for a channel, and the sequence number it is current as of. */
  | { t: 'reset'; ch: string; seq: number; state: unknown }
  /** One state transition. `seq` is contiguous per channel; a gap means resubscribe. */
  | { t: 'ev'; ch: string; seq: number; e: unknown }
  | { t: 'err'; code: ErrorCode; message: string }
  | { t: 'pong'; at: number; serverTime: number };
